/**
 * @file React компонент для Field0 Canvas с WebGL
 * @module membrane/src/visual/field0/Field0Canvas
 * 
 * Рендерит живое поле туманности через WebGL fragment shader.
 */

import { useEffect, useRef, useState } from 'react';
import { Field0Params, paramsToUniforms, DEFAULT_FIELD_PARAMS } from './field0Params';
import { Field0Controller } from './field0Controller';
import { loadFieldParams } from './field0Api';

// Shader источники (встроенные строки)
const vertexShaderSource = `
attribute vec2 a_position;

void main() {
  gl_Position = vec4(a_position, 0.0, 1.0);
}
`;

const fragmentShaderSource = `
precision highp float;

uniform float u_time;
uniform vec2 u_resolution;
uniform vec2 u_mouse;
uniform float u_zoom;
uniform float u_energy;
uniform float u_turbulence;
uniform float u_cohesion;
uniform float u_drift;
uniform vec4 u_palette[4];
uniform vec3 u_layers[3];
uniform vec2 u_driftDirection; // Seed-направление движения (генерируется при загрузке)
uniform vec2 u_dissipationCenter; // Центр рассеивания (от hover/клика)
uniform float u_dissipationStrength; // Сила рассеивания (0.0 = нет, 1.0 = максимальная)
uniform float u_lightIntensity; // Интенсивность света (градиент поля)

vec3 mod289(vec3 x) {
  return x - floor(x * (1.0 / 289.0)) * 289.0;
}

vec4 mod289(vec4 x) {
  return x - floor(x * (1.0 / 289.0)) * 289.0;
}

vec4 permute(vec4 x) {
  return mod289(((x*34.0)+1.0)*x);
}

vec4 taylorInvSqrt(vec4 r) {
  return 1.79284291400159 - 0.85373472095314 * r;
}

float snoise(vec3 v) {
  const vec2 C = vec2(1.0/6.0, 1.0/3.0);
  const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);

  vec3 i = floor(v + dot(v, C.yyy));
  vec3 x0 = v - i + dot(i, C.xxx);

  vec3 g = step(x0.yzx, x0.xyz);
  vec3 l = 1.0 - g;
  vec3 i1 = min(g.xyz, l.zxy);
  vec3 i2 = max(g.xyz, l.zxy);

  vec3 x1 = x0 - i1 + C.xxx;
  vec3 x2 = x0 - i2 + C.yyy;
  vec3 x3 = x0 - D.yyy;

  i = mod289(i);
  vec4 p = permute(permute(permute(
    i.z + vec4(0.0, i1.z, i2.z, 1.0))
    + i.y + vec4(0.0, i1.y, i2.y, 1.0))
    + i.x + vec4(0.0, i1.x, i2.x, 1.0));

  float n_ = 0.142857142857;
  vec3 ns = n_ * D.wyz - D.xzx;

  vec4 j = p - 49.0 * floor(p * ns.z * ns.z);

  vec4 x_ = floor(j * ns.z);
  vec4 y_ = floor(j - 7.0 * x_);

  vec4 x = x_ * ns.x + ns.yyyy;
  vec4 y = y_ * ns.x + ns.yyyy;
  vec4 h = 1.0 - abs(x) - abs(y);

  vec4 b0 = vec4(x.xy, y.xy);
  vec4 b1 = vec4(x.zw, y.zw);

  vec4 s0 = floor(b0)*2.0 + 1.0;
  vec4 s1 = floor(b1)*2.0 + 1.0;
  vec4 sh = -step(h, vec4(0.0));

  vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy;
  vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww;

  vec3 p0 = vec3(a0.xy, h.x);
  vec3 p1 = vec3(a0.zw, h.y);
  vec3 p2 = vec3(a1.xy, h.z);
  vec3 p3 = vec3(a1.zw, h.w);

  vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2, p2), dot(p3,p3)));
  p0 *= norm.x;
  p1 *= norm.y;
  p2 *= norm.z;
  p3 *= norm.w;

  vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
  m = m * m;
  return 42.0 * dot(m*m, vec4(dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3)));
}

float fbm(vec3 p) {
  float value = 0.0;
  float amplitude = 0.5;
  float frequency = 1.0;
  
  for (int i = 0; i < 4; i++) {
    value += amplitude * snoise(p * frequency);
    frequency *= 2.0;
    amplitude *= 0.5;
  }
  
  return value;
}

vec2 mouseDisturbance(vec2 uv, vec2 mouse) {
  vec2 delta = uv - mouse;
  float dist = length(delta);
  float influence = exp(-dist * 8.0) * 0.3;
  return delta * influence;
}

// Мягкое рассеивание тумана при hover/клике
// Рассеивает часть тумана, но не полностью открывает - оставляет легкую дымку
float dissipationEffect(vec2 uv, vec2 center, float strength) {
  if (strength < 0.01) return 1.0; // Нет рассеивания
  
  float dist = length(uv - center);
  // Большой радиус и очень мягкий falloff для плавного перехода
  float radius = 0.5; // Радиус влияния
  float falloff = 0.4; // Очень большая зона мягкого перехода
  
  // Плавная функция рассеивания с очень мягкими краями
  float fade = smoothstep(radius - falloff, radius + falloff, dist);
  
  // Частичное рассеивание - не полностью открываем, оставляем легкую дымку
  // Максимальное рассеивание - до 40% (остается 60% тумана даже в центре)
  float maxDissipation = 0.4;
  float effect = 1.0 - fade * strength * maxDissipation;
  
  // Гарантируем минимальную плотность для легкости
  effect = max(effect, 0.5);
  
  return effect;
}

  // Мягкое сферическое свечение, пробивающееся сквозь облака
  vec3 goldenLightRays(vec2 uv, float time) {
    // Центральный источник света (немного смещен вверх)
    vec2 lightCenter = vec2(0.5, 0.42);
    float distFromLight = length(uv - lightCenter);
    
    // Мягкое сферическое свечение - равномерное во все стороны
    // Используем плавное экспоненциальное затухание
    float centerGlow = exp(-distFromLight * 2.5) * 0.5;
    
    // Ограничиваем максимальную интенсивность
    float totalLight = min(centerGlow, 0.5);
    
    // Цвет свечения: теплый кремово-золотистый
    vec3 goldenColor = vec3(1.0, 0.96, 0.88);
    
    return goldenColor * totalLight;
}

// Свет как градиент поля (не объект, а разлитый свет)
  vec3 fieldLightGradient(vec2 uv, float intensity, float time) {
  // Свет поднимается изнутри, не сверху
    // Очень мягкий градиент для легкости
  float distFromCenter = length(uv - 0.5);
    float lightGradient = 1.0 - smoothstep(0.0, 0.8, distFromCenter) * 0.15;
  
    // Усиление при взаимодействии - легкое свечение
    lightGradient *= (1.0 + intensity * 0.15);
    
    // Добавляем золотистое свечение
    vec3 goldenLight = goldenLightRays(uv, time);
    lightGradient = min(lightGradient * 1.2, 1.0);
    
    // Комбинируем градиент с золотистым светом
    return vec3(lightGradient) + goldenLight;
}

void main() {
  vec2 uv = gl_FragCoord.xy / u_resolution.xy;
  vec2 centered = (uv - 0.5) * u_zoom + 0.5;
  vec2 mouseDist = mouseDisturbance(centered, u_mouse);
  vec2 disturbed = centered + mouseDist * u_turbulence;
  
  // Естественное движение облаков с плавным покачиванием
  // Основное направление движения
  vec2 baseDirection = normalize(u_driftDirection);
  
  // Очень плавное покачивание для естественности (медленные колебания)
  // Используем очень медленные синусоидальные колебания для естественного движения
  float swayX = sin(u_time * 0.04) * 0.08; // Очень легкое покачивание по X
  float swayY = cos(u_time * 0.06) * 0.05; // Очень легкое покачивание по Y
  
  // Комбинируем основное направление с покачиванием
  vec2 naturalDirection = baseDirection + vec2(swayX, swayY);
  naturalDirection = normalize(naturalDirection); // Нормализуем для сохранения скорости
  
  // Плавное движение с естественным покачиванием
  // Используем плавную функцию времени для более естественного движения
  float smoothTime = u_time * (1.0 + sin(u_time * 0.03) * 0.05); // Очень легкие вариации скорости
  vec2 driftOffset = naturalDirection * smoothTime * u_drift;
  
  // Применяем рассеивание (мягкая зона с большим falloff)
  float dissipation = dissipationEffect(centered, u_dissipationCenter, u_dissipationStrength);
  
  vec3 color = vec3(0.0);
  float totalOpacity = 0.0;
  
  // Обрабатываем каждый слой отдельно (избегаем динамической индексации массивов)
  // Слой 0
  vec3 layer0 = u_layers[0];
  float scale0 = layer0.x;
  float speed0 = layer0.y;
  float opacity0 = layer0.z;
  // Добавляем индивидуальное покачивание для каждого слоя (разные частоты, очень плавно)
  float layerSway0 = sin(u_time * 0.03 + scale0 * 0.5) * 0.04;
  vec2 layerOffset0 = driftOffset * speed0 + vec2(layerSway0, cos(u_time * 0.04 + scale0 * 0.5) * 0.03);
  vec2 pos0_2d = disturbed * scale0 + layerOffset0;
  vec3 pos0 = vec3(pos0_2d.x, pos0_2d.y, u_time * speed0 * 0.5);
  float noise0 = fbm(pos0);
  // Более воздушная плотность - меньше плотных облаков, больше легкости
  float density0 = smoothstep(-0.5, 0.5, noise0 * u_cohesion * 0.8);
  float colorMix0 = (noise0 * 0.5 + 0.5) * 3.0;
  float t0 = fract(colorMix0);
  float colorIndex0 = floor(colorMix0);
  if (colorIndex0 < 0.0) colorIndex0 = 0.0;
  if (colorIndex0 > 2.0) colorIndex0 = 2.0;
  vec4 layerColor0;
  if (colorIndex0 < 1.0) {
    layerColor0 = mix(u_palette[0], u_palette[1], t0);
  } else if (colorIndex0 < 2.0) {
    layerColor0 = mix(u_palette[1], u_palette[2], t0);
  } else {
    layerColor0 = mix(u_palette[2], u_palette[3], t0);
  }
  color += layerColor0.rgb * density0 * opacity0;
  totalOpacity += density0 * opacity0;
  
  // Слой 1
  vec3 layer1 = u_layers[1];
  float scale1 = layer1.x;
  float speed1 = layer1.y;
  float opacity1 = layer1.z;
  // Индивидуальное покачивание для слоя 1 (очень плавно)
  float layerSway1 = sin(u_time * 0.035 + scale1 * 0.5) * 0.05;
  vec2 layerOffset1 = driftOffset * speed1 + vec2(layerSway1, cos(u_time * 0.045 + scale1 * 0.5) * 0.035);
  vec2 pos1_2d = disturbed * scale1 + layerOffset1;
  vec3 pos1 = vec3(pos1_2d.x, pos1_2d.y, u_time * speed1 * 0.5);
  float noise1 = fbm(pos1);
  // Более воздушная плотность
  float density1 = smoothstep(-0.5, 0.5, noise1 * u_cohesion * 0.8);
  float colorMix1 = (noise1 * 0.5 + 0.5) * 3.0;
  float t1 = fract(colorMix1);
  float colorIndex1 = floor(colorMix1);
  if (colorIndex1 < 0.0) colorIndex1 = 0.0;
  if (colorIndex1 > 2.0) colorIndex1 = 2.0;
  vec4 layerColor1;
  if (colorIndex1 < 1.0) {
    layerColor1 = mix(u_palette[0], u_palette[1], t1);
  } else if (colorIndex1 < 2.0) {
    layerColor1 = mix(u_palette[1], u_palette[2], t1);
  } else {
    layerColor1 = mix(u_palette[2], u_palette[3], t1);
  }
  color += layerColor1.rgb * density1 * opacity1;
  totalOpacity += density1 * opacity1;
  
  // Слой 2
  vec3 layer2 = u_layers[2];
  float scale2 = layer2.x;
  float speed2 = layer2.y;
  float opacity2 = layer2.z;
  // Индивидуальное покачивание для слоя 2 (очень плавно)
  float layerSway2 = sin(u_time * 0.04 + scale2 * 0.5) * 0.06;
  vec2 layerOffset2 = driftOffset * speed2 + vec2(layerSway2, cos(u_time * 0.05 + scale2 * 0.5) * 0.04);
  vec2 pos2_2d = disturbed * scale2 + layerOffset2;
  vec3 pos2 = vec3(pos2_2d.x, pos2_2d.y, u_time * speed2 * 0.5);
  float noise2 = fbm(pos2);
  // Более воздушная плотность
  float density2 = smoothstep(-0.5, 0.5, noise2 * u_cohesion * 0.8);
  float colorMix2 = (noise2 * 0.5 + 0.5) * 3.0;
  float t2 = fract(colorMix2);
  float colorIndex2 = floor(colorMix2);
  if (colorIndex2 < 0.0) colorIndex2 = 0.0;
  if (colorIndex2 > 2.0) colorIndex2 = 2.0;
  vec4 layerColor2;
  if (colorIndex2 < 1.0) {
    layerColor2 = mix(u_palette[0], u_palette[1], t2);
  } else if (colorIndex2 < 2.0) {
    layerColor2 = mix(u_palette[1], u_palette[2], t2);
  } else {
    layerColor2 = mix(u_palette[2], u_palette[3], t2);
  }
  color += layerColor2.rgb * density2 * opacity2;
  totalOpacity += density2 * opacity2;
  
  if (totalOpacity > 0.0) {
    color /= totalOpacity;
  }
  
  // Применяем рассеивание (туман истончается, но не полностью)
  totalOpacity *= dissipation;
  
  // Цвет становится светлее в зоне рассеивания
  color = mix(color, color * 1.3, (1.0 - dissipation) * 0.4);
  
  // Свет как градиент поля с золотистым свечением
  vec3 lightGradient = fieldLightGradient(centered, u_lightIntensity, u_time);
  
  // Применяем свет: умножаем цвет на градиент
  color = color * lightGradient;
  
  // Золотистое свечение, которое просвечивает сквозь облака
  vec3 goldenGlow = goldenLightRays(centered, u_time);
  
  // В зоне рассеивания золотистый свет просвечивает сильнее
  float glowThroughClouds = 1.0 - totalOpacity * 0.7;
  float glowIntensity = goldenGlow.r * glowThroughClouds * (1.0 + (1.0 - dissipation) * 0.6);
  
  // Добавляем золотистое свечение
  vec3 goldenColor = vec3(1.0, 0.94, 0.82);
  color += goldenColor * glowIntensity * 0.3;
  
  // Мягкая энергия с легкостью
  color *= u_energy;
  
  // Добавляем общую легкость - поднимаем яркость и делаем более воздушным
  // Смешиваем с теплым белым для золотистого оттенка
  vec3 warmWhite = vec3(1.0, 0.98, 0.95); // Теплый белый с легким золотом
  color = mix(color, warmWhite, 0.12); // Смешиваем для легкости
  color = pow(color, vec3(0.90)); // Более мягкая гамма-коррекция для легкости
  
  // Финальная легкость - увеличиваем яркость с теплым оттенком
  color = min(color * 1.18, vec3(1.0));
  
  gl_FragColor = vec4(color, 1.0);
}
`;

interface Field0CanvasProps {
  onFirstClick?: (x: number, y: number) => void;
  className?: string;
  externalParams?: Field0Params; // Внешние параметры (для Layer0Crystallization)
}

/**
 * Компонент Field0Canvas - WebGL рендеринг живого поля
 */
export default function Field0Canvas({ onFirstClick, className, externalParams }: Field0CanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const glRef = useRef<WebGLRenderingContext | null>(null);
  const programRef = useRef<WebGLProgram | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const controllerRef = useRef<Field0Controller>(new Field0Controller());
  const paramsRef = useRef<Field0Params | null>(null);
  const startTimeRef = useRef<number>(Date.now());
  const [hasClicked, setHasClicked] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Фиксированное направление движения облаков (диагональ справа налево, слегка вниз)
  // Вектор нормализован для плавного движения по экрану за 45-55 секунд
  const driftDirectionRef = useRef<[number, number]>([
    -0.8,  // Движение влево (основное направление)
    0.15   // Легкое движение вниз для естественности
  ]);
  
  // Состояние рассеивания (от hover/клика)
  const [dissipationCenter, setDissipationCenter] = useState<[number, number]>([-1, -1]);
  const [dissipationStrength, setDissipationStrength] = useState(0.0);
  const [lightIntensity, setLightIntensity] = useState(0.0);
  
  // Состояние для управления параметрами через UI
  // Дефолтные значения зависят от уровня (externalParams)
  const getInitialPalette = () => {
    if (externalParams) {
      // Для Layer0Crystallization - новые настройки
      return [
        { r: 243, g: 251, b: 255, saturation: 0.11 }, // #FEFFFF - Цвет 1
        { r: 247, g: 253, b: 232, saturation: 0.34 }, // #FCFEF7 - Цвет 2
        { r: 196, g: 241, b: 255, saturation: 0.89 }, // #CAF3FF - Цвет 3
        { r: 233, g: 255, b: 51, saturation: 0.28 }, // #F9FFC6 - Цвет 4
      ];
    }
    // Для первого уровня - дефолтные настройки для 0-го слоя
    return [
      { r: 4, g: 154, b: 221, saturation: 0.62 }, // #63C0EA - Цвет 1
      { r: 140, g: 193, b: 255, saturation: 1.0 }, // #8CC1FF - Цвет 2
      { r: 175, g: 226, b: 255, saturation: 0.17 }, // #F1FAFF - Цвет 3
      { r: 22, g: 205, b: 255, saturation: 0.52 }, // #86E5FF - Цвет 4
    ];
  };
  
  const getInitialScales = () => {
    if (externalParams) {
      return [14.40, 9.70, 2.50];
    }
    return [9.60, 7.00, 2.50]; // Дефолтные значения для 0-го слоя
  };
  
  const getInitialEnergy = () => {
    if (externalParams) {
      return 0.63;
    }
    return 0.64; // Дефолтное значение для 0-го слоя
  };
  
  const [paletteColors, setPaletteColors] = useState<Array<{r: number, g: number, b: number, saturation: number}>>(getInitialPalette());
  const [layerScales, setLayerScales] = useState<[number, number, number]>(getInitialScales() as [number, number, number]);
  const [energy, setEnergy] = useState(getInitialEnergy());
  
  // Состояние для видимости панели и её ширины
  const [panelVisible, setPanelVisible] = useState(false);
  const [panelWidth, setPanelWidth] = useState(320);
  
  // Ref для хранения текущих значений UI (для использования в render цикле)
  const paletteColorsRef = useRef(paletteColors);
  const layerScalesRef = useRef(layerScales);
  const energyRef = useRef(energy);
  
  // Обновляем ref при изменении состояния (синхронно для немедленного применения)
  paletteColorsRef.current = paletteColors;
  layerScalesRef.current = layerScales;
  energyRef.current = energy;
  
  // Обработка клавиатуры для показа/скрытия панели (Ctrl+y)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Проверяем комбинацию Ctrl+y (или Ctrl+Shift+y)
      const isCtrlY = e.ctrlKey && !e.altKey && !e.metaKey && (e.key === 'y' || e.key === 'Y' || e.keyCode === 89);
      
      if (isCtrlY) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        console.log('Ctrl+y pressed, toggling panel. Current state:', panelVisible);
        setPanelVisible(prev => {
          const newState = !prev;
          console.log('Panel visibility changed to:', newState);
          return newState;
        });
      }
    };
    
    // Используем capture phase для перехвата события до других обработчиков
    window.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('keydown', handleKeyDown, true);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [panelVisible]);
  
  // Функция для преобразования RGB + насыщенность в HEX
  const rgbToHex = (r: number, g: number, b: number, saturation: number): string => {
    // Применяем насыщенность: смешиваем с белым
    const white = 255;
    const finalR = Math.round(r * saturation + white * (1 - saturation));
    const finalG = Math.round(g * saturation + white * (1 - saturation));
    const finalB = Math.round(b * saturation + white * (1 - saturation));
    
    const toHex = (n: number) => {
      const hex = Math.round(n).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };
    
    return `#${toHex(finalR)}${toHex(finalG)}${toHex(finalB)}`.toUpperCase();
  };

  // Инициализация WebGL
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const gl = (canvas.getContext('webgl') || canvas.getContext('experimental-webgl')) as WebGLRenderingContext | null;
    if (!gl) {
      const errorMsg = 'WebGL not supported in this browser';
      console.error(errorMsg);
      setError(errorMsg);
      return;
    }

    glRef.current = gl;
    console.log('WebGL context created successfully');

    // Загрузка параметров из Mirror API
    loadFieldParams()
      .then((params) => {
        console.log('Field params loaded:', params);
        paramsRef.current = params;
      })
      .catch((err) => {
        console.warn('Failed to load field params, using defaults:', err);
        paramsRef.current = null; // Будет использован дефолт в render
      });

    // Компиляция shader
    function compileShader(source: string, type: number): WebGLShader | null {
      if (!gl) return null;
      
      const shader = gl.createShader(type);
      if (!shader) return null;

      gl.shaderSource(shader, source);
      gl.compileShader(shader);

      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        const shaderType = type === gl.VERTEX_SHADER ? 'VERTEX' : 'FRAGMENT';
        const errorLog = gl.getShaderInfoLog(shader);
        console.error(`[${shaderType} SHADER] Compile error:`, errorLog);
        console.error(`[${shaderType} SHADER] Source:`, source);
        gl.deleteShader(shader);
        return null;
      }

      return shader;
    }

    // Создание программы
    if (!gl) return;
    
    const vertexShader = compileShader(vertexShaderSource, gl.VERTEX_SHADER);
    const fragmentShader = compileShader(fragmentShaderSource, gl.FRAGMENT_SHADER);

    if (!vertexShader || !fragmentShader) {
      const errorMsg = 'Failed to compile shaders';
      console.error(errorMsg);
      setError(errorMsg);
      return;
    }
    
    console.log('Shaders compiled successfully');

    if (!gl) return;
    
    const program = gl.createProgram();
    if (!program) {
      console.error('Failed to create program');
      return;
    }

    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      const errorMsg = `Program link error: ${gl.getProgramInfoLog(program)}`;
      console.error(errorMsg);
      setError(errorMsg);
      return;
    }

    programRef.current = program;
    console.log('WebGL program linked successfully');

    // Создание полноэкранного квада
    if (!gl) return;
    
    const positionBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([
        -1, -1,
         1, -1,
        -1,  1,
        -1,  1,
         1, -1,
         1,  1,
      ]),
      gl.STATIC_DRAW
    );

    const positionLocation = gl.getAttribLocation(program, 'a_position');
    gl.enableVertexAttribArray(positionLocation);
    gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);

    // Обработка изменения размера
    function resize() {
      if (!canvas || !gl) return;
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      const width = Math.max(1, Math.floor(rect.width * dpr));
      const height = Math.max(1, Math.floor(rect.height * dpr));
      
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
        gl.viewport(0, 0, width, height);
        console.log('Canvas resized:', width, 'x', height);
      }
    }

    // Первоначальная установка размера
    resize();
    window.addEventListener('resize', resize);
    
    // Убеждаемся, что canvas имеет размер
    if (canvas.width === 0 || canvas.height === 0) {
      console.warn('Canvas has zero size, forcing resize');
      // Используем requestAnimationFrame для гарантии, что DOM готов
      requestAnimationFrame(() => {
        resize();
        if (canvas.width === 0 || canvas.height === 0) {
          // Если всё ещё ноль, устанавливаем минимальный размер
          canvas.width = 800;
          canvas.height = 600;
          gl.viewport(0, 0, 800, 600);
          console.log('Forced canvas size to 800x600');
        }
      });
    }

    // Обработка мыши
    function handleMouseMove(e: MouseEvent) {
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;
      controllerRef.current.setMouse(x, 1.0 - y); // Инвертируем Y для GL координат
      
      // Мягкое рассеивание при hover (не резкое, а истончение)
      setDissipationCenter([x, 1.0 - y]);
      setDissipationStrength(0.5); // Умеренная сила для частичного рассеивания
      setLightIntensity(0.3); // Легкое усиление света при взаимодействии
    }
    
    // Обработка ухода мыши
    function handleMouseLeave() {
      // Плавно убираем рассеивание (easing → long → almost imperceptible)
      // Плавность обеспечивается рендер-циклом, который обновляется каждый кадр
      setDissipationStrength(0.0);
      setLightIntensity(0.0);
    }

    // Обработка скролла
    function handleWheel(e: WheelEvent) {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      const currentZoom = controllerRef.current.getState().zoom;
      controllerRef.current.setZoom(currentZoom + delta);
    }

    // Обработка клика - углубление состояния, не переход
    function handleClick(e: MouseEvent) {
      if (!canvas) return;
      
      const rect = canvas.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;
      const xPercent = x * 100;
      const yPercent = (1.0 - y) * 100; // Инвертируем для процентов
      
      // Углубление состояния: туман светлеет, появляется ощущение глубины
      setDissipationCenter([x, 1.0 - y]);
      setDissipationStrength(0.7); // Умеренное рассеивание при клике (не полностью открываем)
      setLightIntensity(0.5); // Умеренное усиление света
      
      // Если это первый клик, уведомляем родителя (но не переходим сразу)
      if (!hasClicked) {
        setHasClicked(true);
        // Плавная задержка перед переходом (если нужен переход)
        setTimeout(() => {
          if (onFirstClick) {
            onFirstClick(xPercent, yPercent);
          }
        }, 800); // Даём время почувствовать углубление
      }
    }

    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseleave', handleMouseLeave);
    canvas.addEventListener('wheel', handleWheel, { passive: false });
    canvas.addEventListener('click', handleClick);

    // Анимационный цикл
    function render() {
      if (!gl || !program || !canvas) {
        console.error('WebGL context, program or canvas not available');
        return;
      }

      // Используем внешние параметры, загруженные параметры или дефолтные
      const baseParams = externalParams || paramsRef.current || DEFAULT_FIELD_PARAMS;
      
      // Если переданы внешние параметры, обновляем ref
      if (externalParams) {
        paramsRef.current = externalParams;
      }

      // Обновляем контроллер
      controllerRef.current.update();
      const controllerState = controllerRef.current.getState();

      // Вычисляем время
      const time = (Date.now() - startTimeRef.current) / 1000.0;

      // Создаем модифицированные параметры с учетом значений из UI (используем ref для актуальных значений)
      const currentPalette = paletteColorsRef.current.map(c => rgbToHex(c.r, c.g, c.b, c.saturation));
      const modifiedParams: Field0Params = {
        ...baseParams,
        palette: currentPalette,
        energy: energyRef.current,
        layers: baseParams.layers.map((layer, i) => ({
          ...layer,
          scale: layerScalesRef.current[i],
        })),
      };
      
      // Отладочный вывод при изменении параметров
      const debugKey = JSON.stringify({ palette: currentPalette, energy: energyRef.current, scales: layerScalesRef.current });
      if (!(window as any).lastDebugKey || (window as any).lastDebugKey !== debugKey) {
        console.log('🎨 Параметры обновлены:', {
          palette: currentPalette,
          energy: energyRef.current,
          scales: layerScalesRef.current
        });
        (window as any).lastDebugKey = debugKey;
      }

      // Преобразуем параметры в uniforms
      let uniforms;
      try {
        uniforms = paramsToUniforms(
          modifiedParams,
          time,
          [canvas.width, canvas.height],
          controllerState.mouse,
          controllerState.zoom
        );
      } catch (err) {
        console.error('Error converting params to uniforms:', err);
        animationFrameRef.current = requestAnimationFrame(render);
        return;
      }

      // Устанавливаем uniforms
      gl.useProgram(program);

      const timeLoc = gl.getUniformLocation(program, 'u_time');
      const resolutionLoc = gl.getUniformLocation(program, 'u_resolution');
      const mouseLoc = gl.getUniformLocation(program, 'u_mouse');
      const zoomLoc = gl.getUniformLocation(program, 'u_zoom');
      const energyLoc = gl.getUniformLocation(program, 'u_energy');
      const turbulenceLoc = gl.getUniformLocation(program, 'u_turbulence');
      const cohesionLoc = gl.getUniformLocation(program, 'u_cohesion');
      const driftLoc = gl.getUniformLocation(program, 'u_drift');
      const driftDirectionLoc = gl.getUniformLocation(program, 'u_driftDirection');
      const dissipationCenterLoc = gl.getUniformLocation(program, 'u_dissipationCenter');
      const dissipationStrengthLoc = gl.getUniformLocation(program, 'u_dissipationStrength');
      const lightIntensityLoc = gl.getUniformLocation(program, 'u_lightIntensity');

      gl.uniform1f(timeLoc, uniforms.u_time);
      gl.uniform2f(resolutionLoc, uniforms.u_resolution[0], uniforms.u_resolution[1]);
      gl.uniform2f(mouseLoc, uniforms.u_mouse[0], uniforms.u_mouse[1]);
      gl.uniform1f(zoomLoc, uniforms.u_zoom);
      gl.uniform1f(energyLoc, uniforms.u_energy);
      gl.uniform1f(turbulenceLoc, uniforms.u_turbulence);
      gl.uniform1f(cohesionLoc, uniforms.u_cohesion);
      gl.uniform1f(driftLoc, uniforms.u_drift);
      
      // Новые uniforms для seed-направления и рассеивания
      gl.uniform2f(driftDirectionLoc, driftDirectionRef.current[0], driftDirectionRef.current[1]);
      gl.uniform2f(dissipationCenterLoc, dissipationCenter[0], dissipationCenter[1]);
      gl.uniform1f(dissipationStrengthLoc, dissipationStrength);
      gl.uniform1f(lightIntensityLoc, lightIntensity);

      // Устанавливаем массив палитры (4 цвета)
      // В WebGL массивы uniforms устанавливаются по элементам
      for (let i = 0; i < 4; i++) {
        const loc = gl.getUniformLocation(program, `u_palette[${i}]`);
        if (loc) {
          gl.uniform4f(
            loc,
            uniforms.u_palette[i * 4 + 0],
            uniforms.u_palette[i * 4 + 1],
            uniforms.u_palette[i * 4 + 2],
            uniforms.u_palette[i * 4 + 3]
          );
        }
      }

      // Устанавливаем массив слоёв (3 слоя)
      for (let i = 0; i < 3; i++) {
        const loc = gl.getUniformLocation(program, `u_layers[${i}]`);
        if (loc) {
          gl.uniform3f(
            loc,
            uniforms.u_layers[i * 3 + 0],
            uniforms.u_layers[i * 3 + 1],
            uniforms.u_layers[i * 3 + 2]
          );
        }
      }

      // Проверяем ошибки WebGL
      const glError = gl.getError();
      if (glError !== gl.NO_ERROR) {
        console.warn('WebGL error:', glError);
      }

      // Рендерим
      gl.drawArrays(gl.TRIANGLES, 0, 6);

      animationFrameRef.current = requestAnimationFrame(render);
    }

    // Запускаем рендер
    render();
    
    // Устанавливаем флаг инициализации после первого кадра
    setTimeout(() => {
      setIsInitialized(true);
    }, 200);

    // Очистка
    return () => {
      window.removeEventListener('resize', resize);
      if (canvas) {
        canvas.removeEventListener('mousemove', handleMouseMove);
        canvas.removeEventListener('mouseleave', handleMouseLeave);
        canvas.removeEventListener('wheel', handleWheel);
        canvas.removeEventListener('click', handleClick);
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [hasClicked, onFirstClick]);

  // Fallback для случаев ошибки или отсутствия WebGL
  if (error) {
    return (
      <div
        className={className}
        style={{
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: '16px',
            left: '16px',
            padding: '8px 10px',
            background: 'rgba(0, 0, 0, 0.45)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '8px',
            color: 'rgba(255, 255, 255, 0.8)',
            fontSize: '12px',
          }}
        >
          ⚠️ {error}
        </div>
      </div>
    );
  }

  return (
    <>
      <canvas
        ref={canvasRef}
        className={className}
        style={{
          width: '100%',
          height: '100%',
          display: 'block',
          cursor: hasClicked ? 'default' : 'crosshair',
          backgroundColor: 'transparent',
        }}
      />
      {!isInitialized && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            color: 'rgba(255, 255, 255, 0.4)',
            fontSize: '12px',
          }}
        >
          Инициализация поля...
        </div>
      )}
      
      {/* Кнопка для показа панели (только в режиме разработки) */}
      {import.meta.env.DEV && !panelVisible && (
        <button
          onClick={() => {
            console.log('Button clicked, showing panel');
            setPanelVisible(true);
          }}
          style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            padding: '8px 16px',
            background: 'rgba(0, 0, 0, 0.7)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            borderRadius: '8px',
            color: 'white',
            fontSize: '12px',
            cursor: 'pointer',
            zIndex: 999,
            fontFamily: 'monospace',
          }}
          title="Показать панель управления (Ctrl+y)"
        >
          🎨 Панель (Ctrl+y)
        </button>
      )}
      
      {/* Панель управления параметрами (только в режиме разработки) */}
      {import.meta.env.DEV && panelVisible && (
        <div
          style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            background: 'rgba(0, 0, 0, 0.8)',
            padding: '15px',
            borderRadius: '12px',
            color: 'white',
            fontSize: '11px',
            fontFamily: 'monospace',
            width: `${panelWidth}px`,
            maxHeight: '90vh',
            overflowY: 'auto',
            zIndex: 1000,
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            resize: 'horizontal',
            overflow: 'auto',
          }}
        >
          <div style={{ marginBottom: '7px', fontWeight: 'bold', fontSize: '13px', borderBottom: '1px solid rgba(255,255,255,0.3)', paddingBottom: '7px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>🎨 Управление облаками</span>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <input
                type="range"
                min="250"
                max="500"
                value={panelWidth}
                onChange={(e) => setPanelWidth(parseInt(e.target.value))}
                style={{ width: '60px', cursor: 'ew-resize', height: '4px' }}
                title="Изменить ширину панели"
              />
              <button
                onClick={() => setPanelVisible(false)}
                style={{
                  padding: '3px 8px',
                  fontSize: '10px',
                  background: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.3)',
                  borderRadius: '4px',
                  color: 'white',
                  cursor: 'pointer',
                }}
                title="Скрыть панель (y)"
              >
                ✕
              </button>
              <button
                onClick={() => {
                  // Сброс к дефолтным значениям (зависит от уровня)
                  if (externalParams) {
                    // Для Layer0Crystallization - новые настройки
                    setPaletteColors([
                      { r: 243, g: 251, b: 255, saturation: 0.11 }, // #FEFFFF - Цвет 1
                      { r: 247, g: 253, b: 232, saturation: 0.34 }, // #FCFEF7 - Цвет 2
                      { r: 196, g: 241, b: 255, saturation: 0.89 }, // #CAF3FF - Цвет 3
                      { r: 233, g: 255, b: 51, saturation: 0.28 }, // #F9FFC6 - Цвет 4
                    ]);
                    setLayerScales([14.40, 9.70, 2.50]);
                    setEnergy(0.63);
                  } else {
                    // Для первого уровня - дефолтные настройки для 0-го слоя
                    setPaletteColors([
                      { r: 4, g: 154, b: 221, saturation: 0.62 }, // #63C0EA - Цвет 1
                      { r: 140, g: 193, b: 255, saturation: 1.0 }, // #8CC1FF - Цвет 2
                      { r: 175, g: 226, b: 255, saturation: 0.17 }, // #F1FAFF - Цвет 3
                      { r: 22, g: 205, b: 255, saturation: 0.52 }, // #86E5FF - Цвет 4
                    ]);
                    setLayerScales([9.60, 7.00, 2.50]);
                    setEnergy(0.64);
                  }
                }}
                style={{
                  padding: '3px 8px',
                  fontSize: '10px',
                  background: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.3)',
                  borderRadius: '4px',
                  color: 'white',
                  cursor: 'pointer',
                }}
                title="Вернуть дефолтные значения"
              >
                Сброс
              </button>
            </div>
          </div>
          <div style={{ marginBottom: '5px', padding: '6px', background: 'rgba(255,255,255,0.05)', borderRadius: '4px', fontSize: '10px', color: 'rgba(255,255,255,0.7)' }}>
            💡 Ctrl+y для показа/скрытия
          </div>
        
          {/* Палитра цветов */}
          <div style={{ marginBottom: '8px' }}>
            <div style={{ marginBottom: '5px', fontWeight: 'bold', fontSize: '12px' }}>Цвета облаков:</div>
            {paletteColors.map((color, colorIndex) => {
              const hexColor = rgbToHex(color.r, color.g, color.b, color.saturation);
              
              const updateColor = (field: 'r' | 'g' | 'b' | 'saturation', value: number) => {
                const newColors = [...paletteColors];
                newColors[colorIndex] = { ...newColors[colorIndex], [field]: value };
                setPaletteColors(newColors);
              };
              
              return (
                <div key={colorIndex} style={{ marginBottom: '7px', padding: '8px', background: 'rgba(255,255,255,0.05)', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.1)' }}>
                  <div style={{ marginBottom: '5px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div
                      style={{
                        width: '40px',
                        height: '24px',
                        backgroundColor: hexColor,
                        border: '2px solid rgba(255, 255, 255, 0.3)',
                        borderRadius: '4px',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                      }}
                    />
                    <div>
                      <div style={{ fontWeight: 'bold', fontSize: '12px' }}>Цвет {colorIndex + 1}</div>
                      <div style={{ fontFamily: 'monospace', color: '#88ff88', fontSize: '10px', marginTop: '1px' }}>{hexColor}</div>
                    </div>
                  </div>
                  
                  {/* Ползунки RGB */}
                  {(['r', 'g', 'b'] as const).map((channel) => (
                    <div key={channel} style={{ marginBottom: '3px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px', fontSize: '10px' }}>
                        <span style={{ textTransform: 'uppercase', fontWeight: 'bold', color: channel === 'r' ? '#ff8888' : channel === 'g' ? '#88ff88' : '#8888ff' }}>
                          {channel}:
                        </span>
                        <span style={{ fontFamily: 'monospace' }}>{color[channel]}</span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="255"
                        value={color[channel]}
                        onChange={(e) => updateColor(channel, parseInt(e.target.value))}
                        style={{ width: '100%', cursor: 'pointer', height: '5px', borderRadius: '3px' }}
                      />
                    </div>
                  ))}
                  
                  {/* Ползунок насыщенности (от белого к цвету) */}
                  <div style={{ marginTop: '5px', paddingTop: '5px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px', fontSize: '10px' }}>
                      <span style={{ fontWeight: 'bold' }}>Насыщенность:</span>
                      <span style={{ fontFamily: 'monospace' }}>{(color.saturation * 100).toFixed(0)}%</span>
                    </div>
                    <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.6)', marginBottom: '2px' }}>
                      (0% = белый, 100% = полный цвет)
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      step="1"
                      value={color.saturation * 100}
                      onChange={(e) => updateColor('saturation', parseInt(e.target.value) / 100)}
                      style={{ width: '100%', cursor: 'pointer', height: '5px', borderRadius: '3px' }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        
          {/* Масштабы слоев */}
          <div style={{ marginBottom: '8px', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '7px' }}>
            <div style={{ marginBottom: '5px', fontWeight: 'bold', fontSize: '12px' }}>Масштабы облаков:</div>
            {layerScales.map((scale, layerIndex) => (
              <div key={layerIndex} style={{ marginBottom: '5px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px', fontSize: '10px' }}>
                  <span style={{ fontWeight: 'bold' }}>Слой {layerIndex + 1}:</span>
                  <span style={{ fontFamily: 'monospace', color: '#88ff88' }}>{scale.toFixed(2)}</span>
                </div>
                <input
                  type="range"
                  min="0.5"
                  max="15.0"
                  step="0.1"
                  value={scale}
                  onChange={(e) => {
                    const newScales = [...layerScales] as [number, number, number];
                    newScales[layerIndex] = parseFloat(e.target.value);
                    setLayerScales(newScales);
                  }}
                  style={{ width: '100%', cursor: 'pointer', height: '5px', borderRadius: '3px' }}
                />
              </div>
            ))}
          </div>
          
          {/* Энергия света */}
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '7px' }}>
            <div style={{ marginBottom: '5px', fontWeight: 'bold', fontSize: '12px' }}>Энергия света:</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px', fontSize: '10px' }}>
              <span>Интенсивность:</span>
              <span style={{ fontFamily: 'monospace', color: '#88ff88' }}>{energy.toFixed(2)}</span>
            </div>
            <input
              type="range"
              min="0.1"
              max="1.5"
              step="0.01"
              value={energy}
              onChange={(e) => setEnergy(parseFloat(e.target.value))}
              style={{ width: '100%', cursor: 'pointer', height: '5px', borderRadius: '3px' }}
            />
          </div>
        </div>
      )}
    </>
  );
}

